import each from 'lodash/each';
import flatMap from 'lodash/flatMap';
import has from 'lodash/has';
import keys from 'lodash/keys';
import omit from 'lodash/omit';
import reduce from 'lodash/reduce';

const UI_MAP_KEY = 'represents';

const resolveUiComponent = (key, type) => {
  switch (key) {
    case 'radio':
      return {
        'ui:widget': 'radio',
      };
    case 'checkboxes':
      return {
        'ui:widget': 'checkboxes',
      };
    case 'signature':
      return {
        'ui:widget': 'signature',
      };
    case 'textarea':
      return {
        'ui:widget': 'textarea',
      };
    case 'comment':
      return {
        'ui:widget': 'comment',
      };
    case 'image':
      return {
        'ui:widget': 'image',
      };
    default:
      return key
        ? {
            'ui:field': key,
          }
        : type
          ? {
              'ui:field': type,
            }
          : {};
  }
};

const mapUiProps = props => {
  if (!props) return;
  const ui = reduce(
    keys(props),
    (uiObject, key) => {
      uiObject[`ui:${key}`] = props[key];
      return uiObject;
    },
    {}
  );
  return ui;
};

const resolveUiProps = prop => {
  const { ui } = prop;
  return {
    ...mapUiProps(ui),
    ...resolveUiComponent(prop[UI_MAP_KEY], prop.type),
  };
};

const resolveValidatorConstraints = (prop, isRequired, anyOfRequired) => {
  if (
    !prop.type ||
    prop.type === 'object' ||
    (prop.ui && (prop.ui.disabled || prop.ui.readonly))
  ) {
    return {};
  }

  const anyOf = {
    anyOfRequired,
  };

  const base = {
    loose: {
      ...anyOf,
    },
    strict: {
      ...anyOf,
      presence: isRequired,
    },
  };

  switch (prop.type) {
    case 'string': {
      const lengthIs = prop.length && { is: prop.length };
      const lengthMax = prop.maxLength && { maximum: prop.maxLength };
      const lengthMin = prop.minLength && { minimum: prop.minLength };
      const lengthProp = (lengthIs || lengthMax || lengthMin) && {
          length: {
            ...lengthIs,
            ...lengthMax,
            ...lengthMin,
          },
        };
      return {
        loose: {
          ...base.loose,
          ...lengthProp,
        },
        strict: {
          ...base.strict,
          ...lengthProp,
        },
      };
    }
    case 'number': {
      return {
        loose: {
          ...base.loose,
          numericalityLoose: true,
        },
        strict: {
          ...base.strict,
          numericality: {
            noString: true,
            strict: true,
          },
        },
      };
    }
    case 'boolean': {
      return {
        loose: {
          ...base.loose,
          boolean: {
            loose: true,
          },
        },
        strict: {
          ...base.strict,
          boolean: isRequired,
        },
      };
    }
    default:
      return base;
  }
};

export const parseUiSchema = (schema, definitions, path) => {
  if (!definitions) {
    definitions = schema.definitions;
  }
  let baseUiSchema = {};
  baseUiSchema = Object.assign({}, baseUiSchema, resolveUiProps(schema));
  const schemaKeys = keys(schema.properties);
  const uiSchema = reduce(
    schemaKeys,
    (uiSchemaObject, key) => {
      const prop = schema.properties[key];
      const ui = prop.ui || {};

      uiSchemaObject[key] = Object.assign(
        {},
        uiSchemaObject[key],
        resolveUiProps({
          ...prop,
          ui: {
            ...ui,
            options: {
              ...ui.options,
              validatorKey: path ? `${path}.${key}` : key,
            }
          }
        })
      );
      if (has(prop, '$ref')) {
        const refSchema = Object.assign(
          {},
          definitions[prop.$ref.split('/')[2]]
        );
        uiSchemaObject[key] = Object.assign(
          {},
          parseUiSchema(refSchema, definitions, path ? `${path}.${key}` : key),
          uiSchemaObject[key],
        );
      }
      if (has(prop, 'properties')) {
        uiSchemaObject[key] = Object.assign(
          {},
          parseUiSchema(prop, definitions, path ? `${path}.${key}` : key),
          uiSchemaObject[key],
        );
      }
      return uiSchemaObject;
    },
    baseUiSchema
  );
  return uiSchema;
};

export const resolveFormControlStateSchema = (
  schema,
  definitions,
  path,
  formControlSchema = {}
) => {
  if (!definitions) {
    definitions = schema.definitions;
  }
  const schemaKeys = keys(schema.properties);
  each(schemaKeys, key => {
    const prop = schema.properties[key];
    formControlSchema[path ? `${path}.${key}` : key] = Object.assign(
      {},
      { dirty: false, touched: false }
    );

    if (has(prop, '$ref')) {
      const refSchema = Object.assign({}, definitions[prop.$ref.split('/')[2]]);
      resolveFormControlStateSchema(
        refSchema,
        definitions,
        path ? `${path}.${key}` : key,
        formControlSchema
      );
    }
    if (has(prop, 'properties')) {
      resolveFormControlStateSchema(
        prop,
        definitions,
        path ? `${path}.${key}` : key,
        formControlSchema
      );
    }
  });
  return formControlSchema;
};

const resolveValidatorSchema = (
  schema,
  definitions,
  mode,
  path,
  validatorSchema = {},
  required = [],
  conditionallyRequired = []
) => {
  if (!definitions) {
    definitions = schema.definitions;
  }
  const schemaKeys = keys(schema.properties);
  each(schemaKeys, key => {
    const initialProp = schema.properties[key];
    const refSchema = has(initialProp, '$ref')
      ? Object.assign({}, definitions[initialProp.$ref.split('/')[2]])
      : {};
    const prop = {
      ...omit(initialProp, '$ref'),
      ...refSchema,
    };
    const isRequired = required.indexOf(key) > -1;
    const anyOfRequired = conditionallyRequired.length
      ? flatMap(conditionallyRequired, item => {
          return item.required.map(
            value => (path ? `${path}.${value}` : value)
          );
        })
      : null;
    const constraints = resolveValidatorConstraints(
      prop,
      isRequired,
      anyOfRequired
    )[mode];
    if (constraints) {
      validatorSchema[path ? `${path}.${key}` : key] = Object.assign(
        {},
        constraints
      );
    }

    if (has(prop, 'properties')) {
      resolveValidatorSchema(
        prop,
        definitions,
        mode,
        path ? `${path}.${key}` : key,
        validatorSchema,
        prop.required,
        prop.anyOf
      );
    }
  });
  return validatorSchema;
};

export const parseValidatorSchema = (schema, definitions) => {
  const validatorSchema = {
    strict: resolveValidatorSchema(schema, definitions, 'strict'),
    loose: resolveValidatorSchema(schema, definitions, 'loose'),
  };

  return validatorSchema;
};



// WEBPACK FOOTER //
// ./src/utils/SchemaParser.js